import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Cable } from 'lucide-react';
import {
  Button,
  DatePicker,
  EmptyState,
  messageFromError,
  PageFrame,
  toast,
  todayIso,
  unwrapAction,
} from '@eo/shared';

import { fetchGoogleStartUrl } from './api/api';
import type { TimelineRow } from './api/types';
import { DaySummary } from './DaySummary';
import { AlreadyLoggedDialog } from './AlreadyLoggedDialog';
import { AnalysisProgress } from './AnalysisProgress';
import { DayTimeline } from './DayTimeline';
import { EditRowDialog } from './EditRowDialog';
import { HIDDEN_SOURCES_PARAM, SETUP_COPY } from './constants';
import {
  applyRowOverrides,
  dismissalKind,
  notLoggedMessage,
  parseHiddenSources,
  rowToLogPayload,
  drawnEntryBody,
  drawnRow,
  toggleHiddenSource,
} from './helpers';
import type { DrawnSpan, RowOverride, TimeTrackingBodyProps } from './types';

export function TimeTrackingBody({
  data,
  onLogEntry,
  onDismiss,
  onUpdateEntry,
  onDeleteEntry,
  onSetTaskType,
  onQueryOptions,
  onAnalyse,
  onLoadAnalysedRange,
}: TimeTrackingBodyProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [busyRowId, setBusyRowId] = useState<string | null>(null);
  // An unconfirmed row has nothing to PATCH yet — its corrections are local and
  // travel with the row when it is confirmed.
  const [rowOverrides, setRowOverrides] = useState<Record<string, RowOverride>>({});
  const [editingRow, setEditingRow] = useState<TimelineRow | null>(null);
  const [drawn, setDrawn] = useState<DrawnSpan | null>(null);
  const [doubleLogRow, setDoubleLogRow] = useState<TimelineRow | null>(null);
  const [analysing, setAnalysing] = useState(false);

  // Every write route acts as the signed-in user, so on someone else's day a
  // write would land on the admin's own ClickUp: nothing here may write.
  const readOnly = data.viewAs !== null;
  const analysed = data.analysedAt !== null;
  const analyse = async () => {
    if (analysing) return;
    setAnalysing(true);
    try {
      await onAnalyse({ date: data.date, refresh: analysed, userEmail: data.viewAs });
    } finally {
      setAnalysing(false);
    }
  };

  // Everything below reads the filtered rows, Confirm all included: a CFO who
  // has narrowed the day to Slack expects that command to log Slack alone.
  const hiddenSources = parseHiddenSources(searchParams.get(HIDDEN_SOURCES_PARAM));
  const rows = applyRowOverrides(data.rows, rowOverrides).filter((r) => !hiddenSources.has(r.kind));

  const onToggleSource = (kind: TimelineRow['kind']) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const value = toggleHiddenSource(hiddenSources, kind);
      if (value) next.set(HIDDEN_SOURCES_PARAM, value);
      else next.delete(HIDDEN_SOURCES_PARAM);
      return next;
    });

  const overrideRow = (rowId: string, patch: RowOverride) =>
    setRowOverrides((prev) => ({ ...prev, [rowId]: { ...prev[rowId], ...patch } }));

  const onDateChange = (value: string) => {
    if (!value) return;
    setRowOverrides({});
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('date', value);
      return next;
    });
  };

  // Entries are placed by start time, so confirming stays sequential — two in
  // flight would race for the same slot.
  const runFor = async (rowId: string, work: () => Promise<unknown>) => {
    if (busyRowId !== null) return;
    setBusyRowId(rowId);
    try {
      await work();
    } finally {
      setBusyRowId(null);
    }
  };

  const logRow = async (row: TimelineRow) => {
    unwrapAction(await onLogEntry({ ...rowToLogPayload(row, data.date), rowId: row.id }));
  };

  if (data.setupState) {
    const copy = SETUP_COPY[data.setupState];
    return (
      <div className="flex flex-col">
        <PageFrame title="CFO Summary" />
        <div className="px-[var(--page-margin)]">
          <EmptyState
            icon={Cable}
            title={copy.title}
            description={copy.description}
            action={
              readOnly ? undefined : (
                <Link
                  to="/settings?tab=integrations"
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Open Settings
                </Link>
              )
            }
          />
        </div>
      </div>
    );
  }

  // A proposal ClickUp already holds would be counted twice, so it is only
  // ever logged one at a time and on purpose.
  const unconfirmed = rows.filter((r) => !r.confirmed && r.loggedAs === null);

  const confirmRow = (row: TimelineRow) =>
    void runFor(row.id, () =>
      logRow(row).catch((err: unknown) => {
        toast.error(
          notLoggedMessage([
            {
              title: row.title,
              reason: messageFromError(err, 'ClickUp did not accept it.'),
            },
          ]),
        );
      }),
    );

  return (
    <div className="flex flex-col">
      <PageFrame
        title="CFO Summary"
        subtitle="Reconstruct the day from your calendar and ClickUp, then confirm what to log."
        badge={
          data.fromSnapshot || data.calendarStale ? (
            <span className="text-xs text-text-tertiary">
              {[
                data.fromSnapshot ? 'As first observed that morning.' : null,
                data.calendarStale ? 'Calendar unavailable — meetings may be missing.' : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          ) : undefined
        }
        controls={
          <DatePicker
            value={searchParams.get('date') ?? data.date}
            onChange={onDateChange}
            className="w-40"
            ariaLabel="Summary date"
            maxDate={todayIso()}
            disabled={busyRowId !== null}
            markedDates={data.analysedDates}
            onViewChange={(from, to) => onLoadAnalysedRange({ from, to, userEmail: data.viewAs })}
          />
        }
      />

      <div className="flex flex-col gap-5 px-[var(--page-margin)] pb-8">
        {readOnly && (
          <p
            role="status"
            className="rounded-8 border border-border-faint bg-muted/60 px-3 py-2 text-sm text-text-secondary"
          >
            Viewing {data.viewAs}'s day — read-only. Blocks open to be read; nothing can be
            confirmed, changed or removed.
          </p>
        )}

        {!analysed && !analysing && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="max-w-sm text-sm text-text-secondary">
              Nothing has been reconstructed for this day yet. Analysing reads ClickUp and your
              calendar and proposes the blocks it finds.
            </p>
            <Button disabled={analysing} onClick={() => void analyse()}>
              {analysing ? 'Analysing…' : 'Analyse this day'}
            </Button>
          </div>
        )}

        {analysed && (
          <DaySummary
            loggedMinutes={rows
              .filter((r) => r.confirmed)
              .reduce((s, r) => s + r.durationMinutes, 0)}
            unconfirmedMinutes={unconfirmed.reduce((s, r) => s + r.durationMinutes, 0)}
            unconfirmedCount={unconfirmed.length}
            busy={busyRowId !== null}
            onConfirmAll={
              readOnly
                ? undefined
                : () =>
                    void runFor('all', async () => {
                      // One entry ClickUp refuses must not cost the CFO the rest of
                      // the day; what was refused stays on screen to be retried.
                      const failures: Array<{ title: string; reason: string }> = [];
                      for (const row of unconfirmed) {
                        try {
                          await logRow(row);
                        } catch (err) {
                          failures.push({
                            title: row.title,
                            reason: messageFromError(err, 'ClickUp did not accept it.'),
                          });
                        }
                      }
                      if (failures.length > 0) toast.error(notLoggedMessage(failures));
                    })
            }
            action={
              <Button
                size="sm"
                variant="secondary"
                disabled={analysing || busyRowId !== null}
                onClick={() => void analyse()}
              >
                {analysing ? 'Re-analysing…' : 'Re-analyse'}
              </Button>
            }
          />
        )}

        {analysing && <AnalysisProgress />}

        {analysed && !analysing && (
          <DayTimeline
            data={{ ...data, rows }}
            hiddenSources={hiddenSources}
            onToggleSource={onToggleSource}
            readOnly={readOnly}
            busyRowId={busyRowId}
            onConfirm={(row) => (row.loggedAs ? setDoubleLogRow(row) : confirmRow(row))}
            onDismiss={(row) =>
              void runFor(row.id, () =>
                onDismiss({
                  body: {
                    date: data.date,
                    kind: dismissalKind(row),
                    ref: row.ref,
                  },
                  rowId: row.id,
                }),
              )
            }
            onDelete={(row) =>
              row.entryId
                ? void runFor(row.id, () => onDeleteEntry({ entryId: row.entryId!, rowId: row.id }))
                : undefined
            }
            onChangeDuration={(row, durationMinutes) => {
              if (row.confirmed && row.entryId) {
                return runFor(row.id, () =>
                  onUpdateEntry({
                    entryId: row.entryId!,
                    patch: { durationMinutes },
                    rowId: row.id,
                  }),
                );
              }
              overrideRow(row.id, { durationMinutes });
            }}
            onToggleBillable={(row) => {
              const variable = !row.variable;
              if (row.confirmed && row.entryId) {
                void runFor(row.id, () =>
                  onUpdateEntry({ entryId: row.entryId!, patch: { variable }, rowId: row.id }),
                );
                return;
              }
              overrideRow(row.id, { variable });
            }}
            onEdit={setEditingRow}
            onMove={(row, startTs) => {
              if (row.confirmed && row.entryId) {
                return runFor(row.id, () =>
                  onUpdateEntry({ entryId: row.entryId!, patch: { startTs }, rowId: row.id }),
                );
              }
              // Dropped at a time, a suggestion has one: it is no longer just where there was room.
              overrideRow(row.id, { startTs, placement: 'clock' });
            }}
            onDraw={setDrawn}
            pendingDraw={drawn}
          />
        )}

        {doubleLogRow && (
          <AlreadyLoggedDialog
            row={doubleLogRow}
            onLog={() => confirmRow(doubleLogRow)}
            onClose={() => setDoubleLogRow(null)}
          />
        )}

        {drawn && (
          <EditRowDialog
            creating
            row={drawnRow(drawn)}
            options={data.options}
            taskTypes={data.taskTypes}
            disabled={busyRowId !== null}
            readOnly={readOnly}
            date={data.date}
            onQueryOptions={onQueryOptions}
            onPatchEntry={() => undefined}
            onSetTaskType={() => undefined}
            onSave={(patch) => {
              const span = drawn;
              void runFor('manual', async () => {
                unwrapAction(
                  await onLogEntry({
                    body: drawnEntryBody(span, patch, data.date),
                    // The block stands on the day under this id while ClickUp is
                    // asked, and the entry that comes back takes its place.
                    tempId: `draft:${span.startTs}`,
                  }),
                );
              });
            }}
            onClose={() => setDrawn(null)}
          />
        )}

        {editingRow && (
          <EditRowDialog
            row={editingRow}
            options={data.options}
            taskTypes={data.taskTypes}
            disabled={busyRowId !== null}
            readOnly={readOnly}
            date={data.date}
            onQueryOptions={onQueryOptions}
            onPatchEntry={(patch) =>
              void runFor(editingRow.id, () =>
                onUpdateEntry({ entryId: editingRow.entryId!, patch, rowId: editingRow.id }),
              )
            }
            onSetTaskType={(taskTypeId) =>
              void onSetTaskType({
                entryId: editingRow.entryId!,
                body: { date: data.date, taskTypeId },
                rowId: editingRow.id,
              })
            }
            onSave={(patch) => overrideRow(editingRow.id, patch)}
            onClose={() => setEditingRow(null)}
          />
        )}

        {!data.googleConnected && !readOnly && (
          <button
            type="button"
            onClick={() =>
              void fetchGoogleStartUrl().then(({ url }) => window.location.assign(url))
            }
            className="self-start text-xs text-text-tertiary underline underline-offset-2 hover:text-text-secondary"
          >
            Connect Google Calendar to include meetings
          </button>
        )}
      </div>
    </div>
  );
}
